import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren
} from 'react';
import { api, type AuthUsersResp, type UsersLoginResp } from './api';
import {
  clearRedirectPath,
  clearToken,
  getRedirectPath,
  getToken,
  setRedirectPath,
  setToken
} from './storage';

interface AuthContextValue {
  token: string;
  user: AuthUsersResp | null;
  loading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<AuthUsersResp | null>;
  completeLogin: (payload: UsersLoginResp) => Promise<void>;
  logout: () => void;
  saveRedirect: (path: string) => void;
  consumeRedirect: () => string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [token, setTokenState] = useState(() => getToken());
  const [user, setUser] = useState<AuthUsersResp | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const currentToken = getToken();
    if (!currentToken) {
      setUser(null);
      return null;
    }
    try {
      const profile = await api.getCurrentUser();
      setUser(profile);
      return profile;
    } catch {
      clearToken();
      setTokenState('');
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = async (payload: UsersLoginResp) => {
    setToken(payload.token);
    setTokenState(payload.token);
    await refreshUser();
  };

  const logout = () => {
    clearToken();
    setTokenState('');
    setUser(null);
  };

  const saveRedirect = (path: string) => {
    setRedirectPath(path);
  };

  const consumeRedirect = () => {
    const redirect = getRedirectPath();
    clearRedirectPath();
    return redirect;
  };

  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      loading,
      isAuthenticated: Boolean(token),
      refreshUser,
      completeLogin,
      logout,
      saveRedirect,
      consumeRedirect
    }),
    [loading, token, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

